import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Calculator, Clock, Ruler, BookOpen, PenTool, Users, FileSpreadsheet, UserPlus, LineChart, Target, Bell } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { DjayAvatar, DjayBubble } from '../../../mascot';
import type { AccountRole } from '../../types';
import { OnboardingProgress } from './OnboardingProgress';
interface OnboardingFollowUpStepProps {
  role: AccountRole;
  groupLevel?: number;
  onComplete: () => void;
  onNavigateToModule?: (moduleId: string) => void;
  onNavigateToMyClass?: () => void;
}
export function OnboardingFollowUpStep({
  role,
  groupLevel = 6,
  onComplete,
  onNavigateToModule,
  onNavigateToMyClass
}: OnboardingFollowUpStepProps) {
  const getRoleConfig = () => {
    switch (role) {
      case 'learner':
        return {
          emotion: 'celebrating' as const,
          message: 'Klaar voor de start! Welk vak wil je als eerste oefenen?',
          gradient: 'from-orange-400 to-amber-400',
          border: 'border-orange-100'
        };
      case 'parent':
        return {
          emotion: 'celebrating' as const,
          message: 'Gelukt! Laten we even kijken wat u allemaal kunt doen op het dashboard.',
          gradient: 'from-emerald-400 to-teal-400',
          border: 'border-emerald-100'
        };
      case 'teacher':
        return {
          emotion: 'excited' as const,
          message: 'Klas aangemaakt! Hoe wilt u uw leerlingen toevoegen?',
          gradient: 'from-indigo-500 to-blue-400',
          border: 'border-indigo-100'
        };
      default:
        return {
          emotion: 'happy' as const,
          message: 'Klaar!',
          gradient: 'from-slate-400 to-slate-500',
          border: 'border-slate-100'
        };
    }
  };
  const config = getRoleConfig();
  const handleModuleClick = (moduleId: string) => {
    if (onNavigateToModule) {
      onNavigateToModule(moduleId);
    }
    onComplete();
  };
  const handleTeacherAction = () => {
    if (onNavigateToMyClass) {
      onNavigateToMyClass();
    }
    onComplete();
  };
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="w-full max-w-md mx-auto" data-id="element-220">
      <div className="flex flex-col items-center mb-6 relative" data-id="element-221">
        <motion.div animate={{
        y: [0, -8, 0]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-222">
          <DjayAvatar emotion={config.emotion} size="lg" data-id="element-223" />
        </motion.div>
        <div className="mt-3 w-full max-w-[260px]" data-id="element-224">
          <DjayBubble message={config.message} position="top" colorScheme={role === 'learner' ? 'amber' : role === 'parent' ? 'emerald' : role === 'teacher' ? 'indigo' : 'slate'} autoDismissMs={0} data-id="element-225" />
        </div>
      </div>

      <div className={`bg-white rounded-3xl p-6 sm:p-8 shadow-lg border border-slate-200 relative overflow-hidden`} data-id="element-226">
        <div className={`absolute top-0 left-0 w-full h-2 bg-gradient-to-r ${config.gradient}`} data-id="element-227" />
        <OnboardingProgress currentStep={role === 'learner' ? 1 : 2} totalSteps={role === 'learner' ? 2 : 3} role={role} data-id="element-228" />

        {role === 'learner' && <div className="space-y-4" data-id="element-229">
            <h2 className="text-xl font-bold text-center text-gray-900 mb-6" data-id="element-230">
              Kies een vak
            </h2>
            <div className="grid grid-cols-2 gap-3" data-id="element-231">
              <button onClick={() => handleModuleClick('REK')} className="flex flex-col items-center justify-center p-4 bg-orange-50 hover:bg-orange-100 rounded-2xl transition-colors border border-orange-100" data-id="element-232">
                <Calculator className="w-8 h-8 text-orange-500 mb-2" data-id="element-233" />
                <span className="font-semibold text-orange-900" data-id="element-234">Rekenen</span>
              </button>
              <button onClick={() => handleModuleClick('GET')} className="flex flex-col items-center justify-center p-4 bg-blue-50 hover:bg-blue-100 rounded-2xl transition-colors border border-blue-100" data-id="element-235">
                <Target className="w-8 h-8 text-blue-500 mb-2" data-id="element-236" />
                <span className="font-semibold text-blue-900" data-id="element-237">Getallen</span>
              </button>
              <button onClick={() => handleModuleClick('MET')} className="flex flex-col items-center justify-center p-4 bg-emerald-50 hover:bg-emerald-100 rounded-2xl transition-colors border border-emerald-100" data-id="element-238">
                <Ruler className="w-8 h-8 text-emerald-500 mb-2" data-id="element-239" />
                <span className="font-semibold text-emerald-900" data-id="element-240">Meten</span>
              </button>
              <button onClick={() => handleModuleClick('SPR')} className="flex flex-col items-center justify-center p-4 bg-purple-50 hover:bg-purple-100 rounded-2xl transition-colors border border-purple-100" data-id="element-241">
                <PenTool className="w-8 h-8 text-purple-500 mb-2" data-id="element-242" />
                <span className="font-semibold text-purple-900" data-id="element-243">Spelling</span>
              </button>
              {groupLevel >= 7 && <>
                  <button onClick={() => handleModuleClick('TAA')} className="flex flex-col items-center justify-center p-4 bg-rose-50 hover:bg-rose-100 rounded-2xl transition-colors border border-rose-100" data-id="element-244">
                    <BookOpen className="w-8 h-8 text-rose-500 mb-2" data-id="element-245" />
                    <span className="font-semibold text-rose-900" data-id="element-246">Taal</span>
                  </button>
                  <button onClick={() => handleModuleClick('TIJD')} className="flex flex-col items-center justify-center p-4 bg-cyan-50 hover:bg-cyan-100 rounded-2xl transition-colors border border-cyan-100" data-id="element-247">
                    <Clock className="w-8 h-8 text-cyan-500 mb-2" data-id="element-248" />
                    <span className="font-semibold text-cyan-900" data-id="element-249">Tijd</span>
                  </button>
                </>}
            </div>
            <Button onClick={onComplete} variant="secondary" className="w-full mt-4" data-id="element-250">
              Later kiezen
            </Button>
            <p className="text-center text-sm text-gray-500 mt-2" data-id="element-251">
              Je kunt dit later altijd nog doen via het menu.
            </p>
          </div>}

        {role === 'parent' && <div className="space-y-6" data-id="element-252">
            <h2 className="text-xl font-bold text-center text-gray-900" data-id="element-253">
              Jouw Dashboard
            </h2>
            <div className="space-y-4" data-id="element-254">
              <div className="flex gap-4 items-start p-4 bg-slate-50 rounded-2xl" data-id="element-255">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-256">
                  <LineChart className="w-5 h-5 text-emerald-600" data-id="element-257" />
                </div>
                <div data-id="element-258">
                  <h3 className="font-semibold text-slate-900" data-id="element-259">
                    Voortgang & Groei
                  </h3>
                  <p className="text-sm text-slate-500" data-id="element-260">
                    Bekijk precies waar je kind sterk in is en wat nog extra
                    aandacht nodig heeft.
                  </p>
                </div>
              </div>
              <div className="flex gap-4 items-start p-4 bg-slate-50 rounded-2xl" data-id="element-261">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-262">
                  <Target className="w-5 h-5 text-emerald-600" data-id="element-263" />
                </div>
                <div data-id="element-264">
                  <h3 className="font-semibold text-slate-900" data-id="element-265">
                    Gerichte Tips
                  </h3>
                  <p className="text-sm text-slate-500" data-id="element-266">
                    Krijg wekelijks tips over hoe je je kind het beste kunt
                    helpen bij lastige onderwerpen.
                  </p>
                </div>
              </div>
              <div className="flex gap-4 items-start p-4 bg-slate-50 rounded-2xl" data-id="element-267">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-268">
                  <Bell className="w-5 h-5 text-emerald-600" data-id="element-269" />
                </div>
                <div data-id="element-270">
                  <h3 className="font-semibold text-slate-900" data-id="element-271">Notificaties</h3>
                  <p className="text-sm text-slate-500" data-id="element-272">
                    Stel in wanneer je updates wilt ontvangen over de
                    oefensessies.
                  </p>
                </div>
              </div>
            </div>
            <Button onClick={onComplete} className="w-full py-6 text-lg bg-emerald-600 hover:bg-emerald-700 text-white" data-id="element-273">
              Naar mijn dashboard
              <ArrowRight className="w-5 h-5 ml-2" data-id="element-274" />
            </Button>
          </div>}

        {role === 'teacher' && <div className="space-y-6" data-id="element-275">
            <h2 className="text-xl font-bold text-center text-gray-900" data-id="element-276">
              Leerlingen toevoegen
            </h2>
            <div className="space-y-3" data-id="element-277">
              <button onClick={handleTeacherAction} className="w-full flex items-center gap-4 p-4 bg-indigo-50 hover:bg-indigo-100 rounded-2xl transition-colors border border-indigo-100 text-left" data-id="element-278">
                <div className="w-10 h-10 rounded-full bg-indigo-200 flex items-center justify-center flex-shrink-0" data-id="element-279">
                  <Users className="w-5 h-5 text-indigo-700" data-id="element-280" />
                </div>
                <div data-id="element-281">
                  <h3 className="font-semibold text-indigo-900" data-id="element-282">
                    Koppelcode delen
                  </h3>
                  <p className="text-sm text-indigo-700/70" data-id="element-283">
                    Laat leerlingen zelf lid worden met een code
                  </p>
                </div>
              </button>
              <button onClick={handleTeacherAction} className="w-full flex items-center gap-4 p-4 bg-indigo-50 hover:bg-indigo-100 rounded-2xl transition-colors border border-indigo-100 text-left" data-id="element-284">
                <div className="w-10 h-10 rounded-full bg-indigo-200 flex items-center justify-center flex-shrink-0" data-id="element-285">
                  <UserPlus className="w-5 h-5 text-indigo-700" data-id="element-286" />
                </div>
                <div data-id="element-287">
                  <h3 className="font-semibold text-indigo-900" data-id="element-288">
                    Handmatig toevoegen
                  </h3>
                  <p className="text-sm text-indigo-700/70" data-id="element-289">
                    Maak zelf accounts aan voor leerlingen
                  </p>
                </div>
              </button>
              <button onClick={handleTeacherAction} className="w-full flex items-center gap-4 p-4 bg-indigo-50 hover:bg-indigo-100 rounded-2xl transition-colors border border-indigo-100 text-left" data-id="element-290">
                <div className="w-10 h-10 rounded-full bg-indigo-200 flex items-center justify-center flex-shrink-0" data-id="element-291">
                  <FileSpreadsheet className="w-5 h-5 text-indigo-700" data-id="element-292" />
                </div>
                <div data-id="element-293">
                  <h3 className="font-semibold text-indigo-900" data-id="element-294">CSV Import</h3>
                  <p className="text-sm text-indigo-700/70" data-id="element-295">
                    Importeer een hele klas tegelijk
                  </p>
                </div>
              </button>
            </div>
            <Button onClick={onComplete} variant="secondary" className="w-full" data-id="element-296">
              Sla over voor nu
            </Button>
            <p className="text-center text-sm text-gray-500 mt-2" data-id="element-297">
              Je kunt later leerlingen toevoegen via 'Mijn Klas'.
            </p>
          </div>}
      </div>
    </motion.div>;
}